import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Link, useNavigate } from "react-router";
import { createChangeRequest } from "./createChangeRequest";
import { getJaDisplayName } from "components/infoChangeUtil.ts";

export default function InfoChangeRequest() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [applicantName, setApplicantName] = useState("");
  const [applicantNameKana, setApplicantNameKana] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [faxNumber, setFaxNumber] = useState("");
  const [requestDelete, setRequestDelete] = useState("");

  const [openCompletionDialog, setOpenCompletionDialog] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  // エラー表示のための状態を追加
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const [loading, setLoading] = useState(false);

  // フォームの入力状態をチェックして、少なくとも1つのフィールドに入力があるかを確認
  useEffect(() => {
    const hasInput =
      email.trim() !== "" ||
      applicantName.trim() !== "" ||
      applicantNameKana.trim() !== "" ||
      phoneNumber.trim() !== "" ||
      faxNumber.trim() !== "" ||
      requestDelete.trim() !== "";

    setIsFormValid(hasInput);
  }, [
    email,
    applicantName,
    applicantNameKana,
    phoneNumber,
    faxNumber,
    requestDelete,
  ]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isFormValid) {
      setError("少なくとも1つのフィールドに入力してください");
      setShowError(true);
      return;
    }

    if (!confirm("申請しますか？")) {
      return;
    }

    try {
      setLoading(true);

      await createChangeRequest(
        email,
        applicantName,
        applicantNameKana,
        phoneNumber,
        faxNumber,
        requestDelete,
      );

      setOpenCompletionDialog(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "会員情報の更新中にエラーが発生しました。",
      );
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCompletionDialog = () => {
    setOpenCompletionDialog(false);
    navigate("/mypage");
  };

  // エラーメッセージを閉じる処理
  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        mt: 8,
        mb: 8,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
        会員情報変更申請
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        変更したい情報を入力してください
      </Typography>

      {/* エラーメッセージを表示するSnackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        sx={{ mt: 1, mb: 3, width: "100%" }}
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              margin="normal"
              fullWidth
              id="email"
              label={getJaDisplayName("email")}
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              fullWidth
              id="registrantName"
              label={getJaDisplayName("applicantName")}
              name="registrantName"
              value={applicantName}
              onChange={(e) => setApplicantName(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              fullWidth
              id="registrantFurigana"
              label={getJaDisplayName("applicantNameKana")}
              name="registrantFurigana"
              value={applicantNameKana}
              onChange={(e) => setApplicantNameKana(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              fullWidth
              id="phoneNumber"
              label={getJaDisplayName("phoneNumber")}
              name="phoneNumber"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              fullWidth
              id="faxNumber"
              label={getJaDisplayName("faxNumber")}
              name="faxNumber"
              value={faxNumber}
              onChange={(e) => setFaxNumber(e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              margin="normal"
              fullWidth
              id="requestDelete"
              label={getJaDisplayName("requestDelete")}
              name="requestDelete"
              value={requestDelete}
              onChange={(e) => setRequestDelete(e.target.value)}
              helperText="退会を希望する場合は、退会申請希望、と入力ください"
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
          <Button
            component={Link}
            to="/mypage"
            variant="outlined"
            sx={{ width: "48%" }}
          >
            戻る
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{ width: "48%" }}
            disabled={!isFormValid} // フォームが無効な場合はボタンを無効化
          >
            申請する
          </Button>
        </Box>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Completion Dialog */}
      <Dialog
        open={openCompletionDialog}
        onClose={handleCloseCompletionDialog}
        aria-labelledby="completion-dialog-title"
      >
        <DialogTitle id="completion-dialog-title">完了</DialogTitle>
        <DialogContent>
          <DialogContentText>申請が完了しました</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseCompletionDialog}
            color="primary"
            autoFocus
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {process.env.NODE_ENV === "development" && (
        <Button
          variant="outlined"
          color="secondary"
          sx={{ mb: 2 }}
          onClick={() => {
            setApplicantName("山田太郎");
            setApplicantNameKana("ヤマダタロウ");
            setPhoneNumber("03-1234-5678");
            setFaxNumber("03-8765-4321");
            setEmail("yamada_taro@example.com");
            setRequestDelete("退会申請希望");
          }}
        >
          テストデータを入力
        </Button>
      )}
    </Container>
  );
}
